import { conversionTrackingConfig } from "../config";
import type { LeadConversionAdapter } from "../contracts";
import { createDeliveryResult } from "../contracts";

export const OPENAI_PIXEL_SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
const OPENAI_PIXEL_SCRIPT_ID = "sr-openai-ads-pixel";

export type OpenAiPixelQueue = {
  (...args: unknown[]): void;
  q?: unknown[][];
};

declare global {
  interface Window {
    oaiq?: OpenAiPixelQueue;
  }
}

export function isOpenAiPixelPath(pathname: string): boolean {
  return pathname === "/" || pathname === "/request" || pathname === "/request/";
}

function getSourcePath(sourceUrl: string): string | undefined {
  try {
    return new URL(sourceUrl, window.location.origin).pathname;
  } catch {
    return undefined;
  }
}

function installOpenAiQueue(): OpenAiPixelQueue {
  if (window.oaiq) return window.oaiq;

  const queue: OpenAiPixelQueue = (...args: unknown[]) => {
    queue.q?.push(args);
  };
  queue.q = [];
  window.oaiq = queue;
  return queue;
}

export function initializeOpenAiPixel(pathname: string): boolean {
  if (!conversionTrackingConfig.openaiPixelEnabled) return false;
  if (!conversionTrackingConfig.openaiPixelId) return false;
  if (conversionTrackingConfig.openaiConsent !== "granted") return false;
  if (!isOpenAiPixelPath(pathname)) return false;

  const queue = installOpenAiQueue();
  if (!document.getElementById(OPENAI_PIXEL_SCRIPT_ID)) {
    queue("consent", true);
    queue("init", {
      pixelId: conversionTrackingConfig.openaiPixelId,
      debug: conversionTrackingConfig.openaiDebug,
    });

    const script = document.createElement("script");
    script.id = OPENAI_PIXEL_SCRIPT_ID;
    script.async = true;
    script.src = OPENAI_PIXEL_SDK_URL;
    document.head.appendChild(script);
  }

  return true;
}

export const openAiPixelLeadAdapter: LeadConversionAdapter = {
  id: "openai-pixel",
  async trackLead(event) {
    if (!conversionTrackingConfig.openaiPixelId) {
      return createDeliveryResult({
        provider: this.id,
        channel: "browser",
        eventName: "lead_created",
        status: "skipped",
        eventId: event.conversionEventId,
        reason: "pixel_id_missing",
      });
    }

    if (event.consent.advertising === "denied") {
      return createDeliveryResult({
        provider: this.id,
        channel: "browser",
        eventName: "lead_created",
        status: "skipped",
        eventId: event.conversionEventId,
        reason: "advertising_consent_denied",
      });
    }

    if (conversionTrackingConfig.openaiConsent !== "granted") {
      return createDeliveryResult({
        provider: this.id,
        channel: "browser",
        eventName: "lead_created",
        status: "skipped",
        eventId: event.conversionEventId,
        reason: "site_consent_not_granted",
      });
    }

    const sourcePath = getSourcePath(event.sourceUrl);
    if (!sourcePath) {
      return createDeliveryResult({
        provider: this.id,
        channel: "browser",
        eventName: "lead_created",
        status: "failed",
        eventId: event.conversionEventId,
        reason: "invalid_source_url",
      });
    }

    if (!isOpenAiPixelPath(sourcePath)) {
      return createDeliveryResult({
        provider: this.id,
        channel: "browser",
        eventName: "lead_created",
        status: "skipped",
        eventId: event.conversionEventId,
        reason: "route_not_allowed",
      });
    }

    if (typeof window.oaiq !== "function") {
      return createDeliveryResult({
        provider: this.id,
        channel: "browser",
        eventName: "lead_created",
        status: "failed",
        eventId: event.conversionEventId,
        reason: "pixel_unavailable",
      });
    }

    window.oaiq("measure", "lead_created", { type: "customer_action" }, { event_id: event.conversionEventId });
    return createDeliveryResult({
      provider: this.id,
      channel: "browser",
      eventName: "lead_created",
      status: "sent",
      eventId: event.conversionEventId,
    });
  },
};
